import { useFetchUpcomingVisits } from '@/api/hooks/useFetchUpcomingVisits'
import { ErrorState } from '@/components/ErrorState'
import ProtectedPage from '@/components/ProtectedPage'
import { VisitExpectations } from '@/components/VisitPage/VisitExpectations'
import { VisitFacts } from '@/components/VisitPage/VisitFacts'
import { FlowHeader } from '@/ui/core/flow-header'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { router, useLocalSearchParams } from 'expo-router'
import { useMemo } from 'react'
import Images from '#/assets/images/image-map'

const goBack = () => (router.canGoBack() ? router.back() : router.replace('/'))

const NotFound = () => (
	<ErrorState
		kind="gone"
		glyph="calendar"
		title="This visit isn't on your calendar"
		line="It may have been cancelled or moved. Your upcoming visits are on the Care tab."
		onAction={goBack}
	/>
)

/**
 * One upcoming visit: when it is, how to get in, and what happens once you are.
 *
 * It was a white card with a photograph in it, which is Home's card — the same component drawn
 * twice with a `variant`. The two have come apart: the picture earns its space on a scrolling
 * screen where it has to catch an eye, and here the date is the page's subject and leads as
 * type, in the booking confirmation's own hero set in black.
 *
 * Two things on it. `VisitFacts` is the date, three rows on hairlines and the action;
 * `VisitExpectations` is the only section, and the reason there is a page rather than a card.
 */
export default function VisitScreen() {
	const { id } = useLocalSearchParams<{ id: string }>()
	const appointmentId = useMemo(() => Number.parseInt(id ?? '', 10), [id])
	const { visits, isLoading } = useFetchUpcomingVisits()

	const visit = visits?.find((candidate) => candidate.appointmentId === appointmentId)

	return (
		<ProtectedPage>
			<Screen preset="fixed" className="flex flex-1" safeAreaEdges={['top']}>
				{/* The header keeps its rule. It came off while the page led with a card, whose
				    own edge was a second separator twelve points below it; a page that opens on
				    type has nothing to divide it from the chrome above, so the line is the
				    division. The page opens on its standard padding under it. */}
				<FlowHeader onBack={goBack} />
				<PageShell photo={Images.BrandPhoto}>
					<Skeleton isLoading={isLoading} className="h-40 w-full">
						{!visit ? (
							<NotFound />
						) : (
							// Thirty-two between the visit and the section under it. The gap is
							// measured from the small print below the button rather than from the
							// button itself, so it reads short — and the two blocks are a page's
							// worth of facts and then a page's worth of reading, which is a
							// bigger change of subject than a section break.
							<VStack gap={32}>
								<VisitFacts visit={visit} />
								{/* What the half hour holds.

								    The forms went with the card. "Before you come in" was a
								    timeline with one beat on it — this visit's pending forms —
								    which Home carries as a row directly under the visit and Care
								    carries in full. It made the page about homework rather than
								    about the visit. */}
								<VisitExpectations visit={visit} />
							</VStack>
						)}
					</Skeleton>
				</PageShell>
			</Screen>
		</ProtectedPage>
	)
}
